import { Component, computed, effect, inject, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { selectLocation, selectLocationOptions, selectMenuItems, selectNotifications, selectRouter } from '../../../store/header/header.selectors';
import { Breadcrumb } from 'primeng/breadcrumb';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRouteSnapshot, Router, RouterModule } from '@angular/router';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Menubar } from 'primeng/menubar';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { loadMenuItems, loadNotifications, } from '../../../store/header/header.actions';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { MenuItem } from 'primeng/api';
import * as authActions from '../../../store/auth/auth.actions';
import * as authSelectors from '../../../store/auth/auth.selectors';
import * as operatorsActions from '../../../store/operators/operators.actions';
import * as operatorsSelectors from '../../../store/operators/operators.selectors';
import { OperatorOption } from '../../../store/header/header.models';
import * as chargersSelectors from '../../../features/chargers/store/charger.selectors';
import * as chargerOverviewSelectors from '../../../features/chargers/store/chargerOverviewStore/chargerOverview.selectors';
import * as rfidSelector from '../../../features/rfids/store/rfid.selectors';
import * as groupSelectors from '../../../features/charger-groups/store/charger-group.selectors';
import * as connectorSelectors from '../../../features/connectors/store/connector.selectors';
import * as locationSelectors from '../../../features/locations/store/location.selectors';
import { buildBreadcrumbs } from '../../../store/header/breadcrumb.utils';
import { ChargerGroupMetadataResponse, ChargerOverviewResponse, ConnectorOverviewResponse, LocationListItemResponse, RfidGroupListItemResponse } from '../../../core/cms-api/models';
import * as companySelector from '../../../features/settings/store/company.selectors';
import { Company } from '../../../core/api/models';
import { ViewportService } from '../../../core/services/viewport.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  imports: [SelectModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    DropdownModule,
    Menubar,
    CommonModule,
    RouterModule,
    Breadcrumb,
    ButtonModule,
    PopoverModule]
})
export class HeaderComponent {
  @ViewChild('opNotifications') opNotifications!: Popover;
  @ViewChild('opUserSettings') opUserSettings!: Popover;
  private store = inject(Store);
  private navigationRouter = inject(Router);
  private translationService = inject(TranslocoService);
  protected viewport = inject(ViewportService);
  home: MenuItem | undefined;

  operators = toSignal(this.store.select(operatorsSelectors.selectOperators));
  currentOperator = toSignal(this.store.select(operatorsSelectors.selectCurrentOperator));
  selectedOperator = computed(() => { return { id: this.currentOperator()?.id, name: this.currentOperator()?.name } as OperatorOption });
  operatorsOptions = computed(() => this.operators()?.map(operator => { return { id: operator.id, name: operator.name } as OperatorOption }));
  locations = toSignal(this.store.select(selectLocationOptions));
  selectedLocation = toSignal(this.store.select(selectLocation));
  notifications = toSignal(this.store.select(selectNotifications));
  unreadNotificationCount = computed(() => this.notifications()?.filter(x => !x.isRead).length ?? 0)
  menuItems = toSignal(this.store.select(selectMenuItems));
  chargers = toSignal(this.store.select(chargersSelectors.selectChargerList));
  router = toSignal(this.store.select(selectRouter));
  chargerOverview = toSignal(this.store.select(chargerOverviewSelectors.selectCurrentChargerOverview));
  rfidGroupOverview = this.store.selectSignal(rfidSelector.selectRfidGroupOverview);
  groupOverview = this.store.selectSignal(groupSelectors.currentGroupOverview);
  connectorOverview = this.store.selectSignal(connectorSelectors.selectCurrentConnector);
  locationOverview = this.store.selectSignal(locationSelectors.selectCurrentLocation);
  companyOverview = this.store.selectSignal(companySelector.selectCompanyOverview);
  user = toSignal(this.store.select(authSelectors.selectUser));
  refreshToken = toSignal(this.store.select(authSelectors.selectRefreshToken));
  isChardiObserver = toSignal(this.store.select(authSelectors.selectIsChardiObserver));
  isCompanyAdmin = toSignal(this.store.select(authSelectors.selectIsCompanyAdmin));
  isInternalBillingCustomerOnly = toSignal(this.store.select(authSelectors.selectIsInternalBillingCustomerOnly));

  hideMenuAndOperatorSelection = computed(() =>
    this.isChardiObserver() || this.isCompanyAdmin() || this.isInternalBillingCustomerOnly()
  );


  userInitials = computed(() => {
    const userData = this.user();
    if (!userData?.firstName && !userData?.lastName) {
      return userData?.email?.charAt(0).toUpperCase() || '?';
    }
    const firstInitial = userData.firstName?.charAt(0).toUpperCase() || '';
    const lastInitial = userData.lastName?.charAt(0).toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  });

  /** Flat key->text map of the active language, so breadcrumbs recompute on a language switch. */
  private translation = toSignal(this.translationService.selectTranslation());

  styledBreadcrumbs = computed(() => {
    const router = this.router();
    const translation = this.translation() ?? {};
    const chargerOverview = this.chargerOverview();
    const rfidGroupOverview = this.rfidGroupOverview();
    const groupOverview = this.groupOverview();
    const connectorOverview = this.connectorOverview();
    const locationOverview = this.locationOverview();
    const companyOverview = this.companyOverview();

    const state = router?.state;
    if (!state) return [];

    const chargerNameMap = this.createChargerNameMap(chargerOverview);
    const rfidGroupNameMap = this.createRfidNameMap(rfidGroupOverview);
    const chargerGroupNameMap = this.createChargerGroupNameMap(groupOverview);
    const currentConnectorNameMap = this.createConnectorNameMap(connectorOverview);
    const currentLocationNameMap = this.createLocationNameMap(locationOverview);
    const companyNameMap = this.createCompanyNameMap(companyOverview);

    const breadcrumbResolvers = {
      ':chargerId': (route: ActivatedRouteSnapshot) => {
        const guid = route.params['chargerId'];
        return chargerNameMap[guid] || '';
      },
      ':rfidGroupId': (route: ActivatedRouteSnapshot) => {
        const guid = route.params['rfidGroupId'];
        return rfidGroupNameMap[guid] || '';
      },
      ':groupId': (route: ActivatedRouteSnapshot) => {
        const guid = route.params['groupId'];
        return chargerGroupNameMap[guid] || '';
      },
      ':connectorId/:chargerId': (route: ActivatedRouteSnapshot) => {
        const guid = route.params['connectorId'];
        return currentConnectorNameMap[guid] || '';
      },
      ':locationId': (route: ActivatedRouteSnapshot) => {
        const guid = route.params['locationId'];
        return currentLocationNameMap[guid] || '';
      },
      ':companyId': (route: ActivatedRouteSnapshot) => {
        const id = route.params['companyId'];
        return companyNameMap[id] || '';
      }
    };

    const translate = (key: string) => (translation[key] as string) ?? key;

    return buildBreadcrumbs(state.root, '', [], breadcrumbResolvers, translate);
  });

  userSettingsOptions: { icon: string | null; text: string; action: (() => void) | null }[] = [
    {
      icon: null,
      text: 'Einstellungen',
      action: () => this.navigationRouter.navigate(['companies/companyUsers', this.user()?.id])

    },
    {
      icon: 'fa-light fa-right-from-bracket',
      text: 'Logout',
      action: () => {
        const userData = this.user();
        this.store.dispatch(authActions.submitLogoutRequest({ email: userData!.email!, refreshToken: this.refreshToken() }));
      }
    }
  ];

  createRfidNameMap(rfidGroupOverview: RfidGroupListItemResponse | null | undefined): Record<string, string> {
    const map: Record<string, string> = {}
    if (!rfidGroupOverview)
      return map;
    map[rfidGroupOverview.id] = rfidGroupOverview.name
    return map;
  }

  createChargerNameMap(charger: ChargerOverviewResponse | null | undefined): Record<string, string> {
    const map: Record<string, string> = {};
    if (!charger)
      return map;

    map[charger.id!] = charger.chargerId!
    return map;
  }

  createChargerGroupNameMap(group: ChargerGroupMetadataResponse | null | undefined): Record<string, string> {
    const map: Record<string, string> = {}
    if (!group)
      return map;
    map[group.id] = group.name
    return map;
  }

  createConnectorNameMap(connector: ConnectorOverviewResponse | null | undefined): Record<string, string> {
    const map: Record<string, string> = {}
    if (!connector)
      return map;
    map[connector.id!] = connector.name??`${connector.chargerId}-${connector.connectorId}`;
    return map;
  }
   
  createLocationNameMap(location: LocationListItemResponse | null | undefined): Record<string, string> {
    const map: Record<string, string> = {}
    if (!location)
      return map;
    map[location.id!] = location.name || '';
    return map;
  }

  createCompanyNameMap(companyOverview: Company | null | undefined): Record<number, string> {
    const map: Record<number, string> = {}
    if (!companyOverview)
      return map;
    map[companyOverview.id!] = companyOverview.name
    return map;
  }

  //FormControls
  operatorFormControl = new FormControl(this.selectedOperator());
  locationFormControl = new FormControl(this.selectedLocation())

  constructor() {
    this.store.dispatch(loadMenuItems());
    this.store.dispatch(loadNotifications());

    effect(() => {
      const operator = this.selectedOperator();
      this.operatorFormControl.setValue(operator, { emitEvent: false });
    });

    effect(() => {
      const location = this.selectedLocation();
      this.locationFormControl.setValue(location, { emitEvent: false });
    });
    this.home = { icon: 'pi pi-home', routerLink: '/' };
  }

  onOperatorChange($event: SelectChangeEvent) {
    console.log($event.value);
    this.store.dispatch(operatorsActions.SetCurrentOperator({ currentOperatorId: $event.value.id }));
  }

  onLocationChange($event: SelectChangeEvent) {
    console.log($event.value);
  }

  toggleNotifications(event: MouseEvent): void {
    this.opNotifications.toggle(event);
  }

  toggleUserSettings(event: MouseEvent): void {
    this.opUserSettings.toggle(event);
  }

  onNotificationPopoverShow() {
    setTimeout(() => this.opNotifications?.align(), 10);
  }

  onUserSettingsPopoverShow() {
    setTimeout(() => this.opUserSettings?.align(), 10);
  }
}
